import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { ThumbsDown, ThumbsUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api, errMsg, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Stars } from "@/components/common/Badges";
import { Loading } from "@/components/common/States";
import ReportButton from "@/components/common/ReportButton";

function ReviewForm({ productId, onDone }) {
  const [f, setF] = useState({ rating: 0, title: "", body: "", pros: "", cons: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.rating) { toast.error("Lütfen yıldız puanı verin"); return; }
    setBusy(true);
    try {
      await api.post(`/products/${productId}/reviews`, { ...f, pros: f.pros.split("\n"), cons: f.cons.split("\n") });
      toast.success("Değerlendirmeniz yayımlandı");
      onDone();
    } catch (e2) { toast.error(errMsg(e2)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="panel space-y-3 p-5" data-testid="review-form">
      <p className="font-display font-semibold">Bu modeli değerlendir</p>
      <Stars value={f.rating} size={6} onChange={(n) => setF({ ...f, rating: n })} testId="review-rating" />
      <Input data-testid="review-title-input" placeholder="Başlık" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} />
      <Textarea data-testid="review-body-input" placeholder="Deneyiminiz (en az 10 karakter)" value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} />
      <div className="grid gap-3 sm:grid-cols-2">
        <Textarea data-testid="review-pros-input" placeholder="Olumlu yönler (her satıra bir tane)" value={f.pros} onChange={(e) => setF({ ...f, pros: e.target.value })} />
        <Textarea data-testid="review-cons-input" placeholder="Olumsuz yönler (her satıra bir tane)" value={f.cons} onChange={(e) => setF({ ...f, cons: e.target.value })} />
      </div>
      <Button disabled={busy} data-testid="review-submit-button" className="bg-[#0071e3] hover:bg-[#0077ed] text-white">Gönder</Button>
    </form>
  );
}

/** Reviews belong to the exact catalog model (incl. revision), shared across all its listings. */
export default function ProductReviews({ productId, productTitle }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["reviews", productId], queryFn: async () => (await api.get(`/products/${productId}/reviews`)).data });
  if (isLoading) return <Loading />;
  const mine = data.items.some((r) => r.user_id === user?.id);
  return (
    <section className="space-y-4" data-testid="product-reviews">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="font-display text-xl font-semibold">Ürün modeli değerlendirmeleri</h2>
        <span className="text-xs text-zinc-500">({productTitle} — tüm satıcıların ilanlarında ortak)</span>
      </div>
      <div className="flex items-center gap-3" data-testid="product-rating-summary">
        <span className="font-display text-4xl font-semibold">{data.summary.average ?? "–"}</span>
        <div><Stars value={data.summary.average || 0} /><p className="text-xs text-zinc-500">{data.summary.count} değerlendirme · ürün puanı (satıcı puanından bağımsız)</p></div>
      </div>
      {user && !mine && <ReviewForm productId={productId} onDone={() => qc.invalidateQueries({ queryKey: ["reviews", productId] })} />}
      {!user && <p className="text-sm text-zinc-500">Değerlendirme yazmak için giriş yapın.</p>}
      {data.items.length === 0 ? <p className="text-sm text-zinc-500" data-testid="no-reviews">Bu model için henüz değerlendirme yok.</p> : (
        <ul className="space-y-3">
          {data.items.map((r) => (
            <li key={r.id} className="panel p-4" data-testid={`review-${r.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2"><Stars value={r.rating} size={3} /><span className="font-medium">{r.title}</span></div>
                <span className="text-xs text-zinc-500">{r.user_name} · {formatDate(r.created_at)}</span>
              </div>
              <p className="mt-2 text-sm text-zinc-700 whitespace-pre-line">{r.body}</p>
              <div className="mt-2 grid gap-2 text-xs sm:grid-cols-2">
                {r.pros?.length > 0 && <ul className="text-emerald-700">{r.pros.map((p) => <li key={p} className="flex gap-1"><ThumbsUp className="h-3 w-3 mt-0.5" />{p}</li>)}</ul>}
                {r.cons?.length > 0 && <ul className="text-red-700">{r.cons.map((p) => <li key={p} className="flex gap-1"><ThumbsDown className="h-3 w-3 mt-0.5" />{p}</li>)}</ul>}
              </div>
              <div className="mt-2"><ReportButton targetType="product_review" targetId={r.id} /></div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
